/* eslint-disable react-hooks/exhaustive-deps */
import { CloseOutlined } from '@ant-design/icons'
import { Input, Modal, notification, Select, Checkbox } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Rule } from 'antd/es/form'

import { useUpdateDepot } from '../../../hooks/useUpdateDepot'
import useGetSapGeo from '../../../hooks/useGetSapGeo'
import { Coordinates, Depot } from '../../../types'
import MapComponent from '../../Map'
import { StyledForm, StyledRow } from '../../StyledComponents'
import useGetAssignmentZones from '../../../hooks/useGetAssignmentZones'

type Props = {
  onClose: () => void
  selectedDepot: Depot
}

const commonValidations = {
  min: 3,
  max: 50,
}

const formValidations = {
  tag: { ...commonValidations },
  street: { ...commonValidations },
  number: { max: 15 },
}

const EditDepotModal: React.FC<Props> = ({ selectedDepot, onClose }) => {
  const [form] = StyledForm.useForm()
  const { resetFields } = form
  const { t } = useTranslation()
  const { sapGeos, sapGeoIsLoading } = useGetSapGeo()
  const { zones, assignmentZoneIsLoading } = useGetAssignmentZones()
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [country, setCountry] = useState('')
  const [coordinates, setCoordinates] = useState<Coordinates[] | undefined>()
  const [isNoNumber, setIsNoNumber] = useState<boolean>(false)
  const [fullAddress, setFullAddress] = useState('')

  const resetModal = () => {
    resetFields()
    setCity('')
    setState('')
    setCountry('')
    setCoordinates(undefined)
    onClose()
  }

  const { updateDepot } = useUpdateDepot(resetModal)

  const validateLocation = (_rule: Rule, value: any) => {
    if (!value) {
      return Promise.reject(t('globals.obligatory_field'))
    }
    return Promise.resolve()
  }

  const validationRules = {
    required: () => ({
      required: true,
      message: t('globals.obligatory_field'),
    }),
    minLength: (min: number) => ({
      min,
      message: t('depot_modal.min_length_error', { min }),
    }),
    maxLength: (max: number) => ({
      max,
      message: t('depot_modal.max_length_error', { max }),
    }),
    numeric: () => ({
      pattern: /^[0-9]+$/,
      message: t('depot_modal.numeric_error'),
    }),
  }

  const ModalTitle = styled.div`
    margin-bottom: 20px;
  `

  const onOk = useCallback(() => {
    form
      .validateFields()
      .then((values: any) => {
        if (!coordinates?.length) {
          notification.error({ message: t('depot_modal.coordinates_required') })
          throw new Error('Coordinates are required')
        }

        const { tag, street, number, assignmentZone } = values

        const location = {
          type: 'Point',
          coordinates: [coordinates[0].lat, coordinates[0].lng] as [number, number],
        }

        console.log('number on update :>> ', number)
        console.log('isNoNumber :>> ', isNoNumber) // ✅ ahora se actualizará correctamente

        updateDepot({
          depotId: selectedDepot.id,
          depotData: {
            tag,
            street,
            number: isNoNumber ? 'S/N' : number || '',
            city,
            state,
            country,
            assignmentZoneId: assignmentZone,
            location,
          },
        })
      })
      .catch(console.error)
  }, [coordinates, selectedDepot, isNoNumber, form, city, state, country, t, updateDepot])

  const handleNumericKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const charCode = event.charCode
    if (charCode < 48 || charCode > 57) {
      event.preventDefault()
    }
  }

  const initialValues = useMemo(() => {
    if (selectedDepot) {
      const { sapGeo, ...supplierData } = selectedDepot
      return {
        ...supplierData,
        number: selectedDepot.number === 'S/N' ? null : selectedDepot.number,
        sapGeo: sapGeo?.id || undefined,
        assignmentZone: supplierData.assignmentZone?.id || undefined,
        country: 'AR',
        city: city || '',
        state: state || '',
      }
    }
    return { country: 'AR', sapGeo: undefined, city: '', state: '' }
  }, [selectedDepot])

  const initialCoordinates: Coordinates[] | undefined = useMemo(() => {
    return [
      {
        lat: selectedDepot.location.coordinates[0],
        lng: selectedDepot.location.coordinates[1],
        name: selectedDepot.tag,
        id: selectedDepot.id,
      },
    ]
  }, [selectedDepot])

  useEffect(() => {
    if (selectedDepot) {
      setCountry(selectedDepot.country || 'AR')
      setCity(selectedDepot.city || '')
      setState(selectedDepot.state || '')
      setCoordinates(initialCoordinates) // Set initial coordinates
    }
  }, [selectedDepot, initialCoordinates])

  const handleAddressSelect = (address: {
    city?: string
    state?: string
    country?: string
    street?: string
    number?: string
  }) => {
    console.log('address :>> ', address)
    setCity(address.city ?? '')
    setState(address.state ?? '')
    setCountry(address.country ?? '')

    setIsNoNumber(!address.number || address.number === '&')

    form.setFieldsValue({
      city: address.city ?? '',
      state: address.state ?? '',
      country: address.country ?? '',
      street: address.street ?? '',
      number: address.number ?? '',
    })
  }

  const updateMapFromForm = () => {
    const { street, number } = form.getFieldsValue(['street', 'number'])
    if (!street || (!isNoNumber && !number) || !city) return

    const address = [street, isNoNumber ? '' : number, city, state, country]
      .filter(Boolean)
      .join(', ')
    setFullAddress(address)
  }

  const handleCheckboxChange = (e: any) => {
    const checked = e.target.checked
    console.log('checked :>> ', checked)
    console.log('isNoNumber 1 :>> ', isNoNumber)
    setIsNoNumber(checked)
    if (checked) {
      form.setFieldsValue({ number: null })
    } else {
      const currentNumber = form.getFieldValue('number')
      if (!currentNumber) {
        form.setFieldsValue({ number: selectedDepot.number === 'S/N' ? '' : selectedDepot.number })
      }
    }
  }

  useEffect(() => {
    if (selectedDepot) {
      const isNoNumberValue = selectedDepot.number === 'S/N'
      setIsNoNumber(isNoNumberValue)
      form.setFieldsValue({
        number: isNoNumberValue ? null : selectedDepot.number,
      })
    }
  }, [selectedDepot])

  return (
    <Modal
      open
      width={1000}
      keyboard
      maskClosable
      onCancel={resetModal}
      title={<ModalTitle>{t('depot_modal.edit_title')}</ModalTitle>}
      closeIcon={<CloseOutlined />}
      okText={t('depot_modal.button_save')} // Change button text to "Guardar"
      cancelText={t('depot_modal.button_cancel')}
      destroyOnClose
      onOk={onOk}
    >
      <div style={{ display: 'flex', alignItems: 'stretch', gap: '16px' }}>
        <div style={{ flex: 1 }}>
          <StyledForm form={form} initialValues={initialValues}>
            <StyledForm.Item hidden name="depotId">
              <Input type="number" />
            </StyledForm.Item>

            <StyledRow className="carrier-form-row">
              <StyledForm.Item
                name="tag"
                label={t('depot_modal.name')}
                rules={[
                  validationRules.required(),
                  validationRules.minLength(formValidations.tag.min),
                  validationRules.maxLength(formValidations.tag.max),
                ]}
                hasFeedback
              >
                <Input />
              </StyledForm.Item>
            </StyledRow>

            <StyledRow className="carrier-form-row">
              <StyledForm.Item
                name="street"
                label={t('depot_modal.street')}
                rules={[
                  validationRules.required(),
                  validationRules.minLength(formValidations.street.min),
                  validationRules.maxLength(formValidations.street.max),
                ]}
                hasFeedback
              >
                <Input />
              </StyledForm.Item>
            </StyledRow>

            <StyledRow className="carrier-form-row" style={{ marginBottom: 8 }}>
              <>
                <StyledForm.Item
                  name="number"
                  label={t('depot_modal.number')}
                  rules={!isNoNumber ? [validationRules.required(), validationRules.numeric()] : []}
                  hasFeedback
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Input type="number" onKeyPress={handleNumericKeyPress} disabled={isNoNumber} />
                </StyledForm.Item>

                <Checkbox
                  onChange={handleCheckboxChange}
                  checked={isNoNumber}
                  style={{ marginLeft: 2 }}
                >
                  {t('depot_modal.no_number')}
                </Checkbox>
              </>
            </StyledRow>
            <StyledRow className="carrier-form-row">
              <StyledForm.Item
                name="assignmentZone"
                label={t('depot_modal.assigmemnt_zone')}
                hasFeedback
              >
                <Select
                  className="custom-select"
                  showSearch
                  allowClear
                  loading={assignmentZoneIsLoading}
                  filterOption={(input, option) => {
                    const combined = option?.props['data-combined'] ?? ''
                    return combined.toLowerCase().includes(input.toLowerCase())
                  }}
                  optionLabelProp="children"
                >
                  {zones?.map(item => (
                    <Select.Option
                      key={item.id}
                      value={item.id}
                      data-combined={`${item.name} ${item.code}`}
                    >
                      {`${item.name} (${item.code})`}
                    </Select.Option>
                  ))}
                </Select>
              </StyledForm.Item>
            </StyledRow>

            <StyledRow className="carrier-form-row">
              <StyledForm.Item
                name="sapGeo"
                label={t('depot_modal.location')}
                rules={[{ validator: validateLocation }]}
                required
                hasFeedback
              >
                <Select
                  className="custom-select"
                  showSearch
                  loading={sapGeoIsLoading}
                  filterOption={(input, option) => {
                    const combined = option?.props['data-combined'] ?? ''
                    return combined.toLowerCase().includes(input.toLowerCase())
                  }}
                  optionLabelProp="children"
                  onChange={value => {
                    const selectedSapGeo = sapGeos?.find(sapGeo => sapGeo.id === value)
                    setCountry(selectedSapGeo?.country ?? '')
                    setCity(selectedSapGeo?.description ?? '')
                    setState(selectedSapGeo?.region ?? '')
                    setTimeout(() => {
                      updateMapFromForm()
                    }, 100)
                  }}
                  style={{ width: '100%' }}
                  dropdownStyle={{ width: 400 }}
                >
                  {sapGeos?.map(item => (
                    <Select.Option
                      key={item.id}
                      value={item.id}
                      data-combined={`${item.cityCode} ${item.description} ${item.region}`}
                    >
                      {`${item.cityCode} - ${item.description}, ${item.region}`}
                    </Select.Option>
                  ))}
                </Select>
              </StyledForm.Item>
            </StyledRow>

            <StyledRow className="carrier-form-row">
              <StyledForm.Item label={t('depot_modal.city')}>
                <span className="ticket-field-value">{city}</span>
              </StyledForm.Item>
            </StyledRow>

            <StyledRow className="carrier-form-row">
              <StyledForm.Item label={t('depot_modal.province')}>
                <span className="ticket-field-value">{state}</span>
              </StyledForm.Item>
            </StyledRow>

            <StyledRow className="carrier-form-row">
              <StyledForm.Item label={t('depot_modal.country')}>
                <span className="ticket-field-value">{country}</span>
              </StyledForm.Item>
            </StyledRow>
          </StyledForm>
        </div>
        <div style={{ width: '1px', backgroundColor: '#ccc' }} />
        <div style={{ flex: 1 }}>
          <MapComponent
            key={selectedDepot.tag}
            initialCoordinates={initialCoordinates}
            coordinates={coordinates}
            setCoordinates={setCoordinates}
            editable={true}
            fullAddress={fullAddress}
            onAddressSelect={handleAddressSelect}
          />
        </div>
      </div>
    </Modal>
  )
}

export { EditDepotModal }
